(function () {
  if (customElements.get('floral-carousel')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class FloralCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.nav = this.querySelector('[data-carousel-nav]');
      this.dotsEl = this.querySelector('[data-carousel-dots]');
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      if (!this.track) return;

      this.breakpoint = Number(this.dataset.breakpoint || 749);
      this.mq = window.matchMedia('(max-width: ' + this.breakpoint + 'px)');
      this.pageCount = 1;
      this.perView = 1;
      this.currentPage = 0;
      this.raf = 0;
      this.active = false;

      this.onMedia = () => this.sync();
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = 0;
          this.syncFromScroll();
        });
      };
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);
      this.onKey = (event) => this.handleKey(event);
      this.onResize = () => this.layout();
      this.onBlockSelect = (event) => this.handleBlockSelect(event);

      this.mq.addEventListener('change', this.onMedia);
      document.addEventListener('shopify:section:load', this.onMedia);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.ro = new ResizeObserver(this.onResize);
      this.sync();
    }

    disconnectedCallback() {
      this.deactivate();
      if (this.mq) this.mq.removeEventListener('change', this.onMedia);
      document.removeEventListener('shopify:section:load', this.onMedia);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.ro) this.ro.disconnect();
    }

    get cells() {
      return Array.from(this.track.children);
    }

    isRtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    sync() {
      if (this.mq.matches) this.activate();
      else this.deactivate();
    }

    activate() {
      if (!this.active) {
        this.active = true;
        this.track.tabIndex = 0;
        this.track.setAttribute('role', 'group');
        this.track.setAttribute('aria-roledescription', 'carousel');
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('keydown', this.onKey);
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
        this.ro.observe(this.track);
      }
      this.layout();
    }

    deactivate() {
      if (!this.track) return;
      if (this.active) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKey);
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
        this.ro.unobserve(this.track);
      }
      this.active = false;
      this.track.removeAttribute('tabindex');
      this.track.removeAttribute('role');
      this.track.removeAttribute('aria-roledescription');
      if (this.nav) this.nav.hidden = true;
    }

    layout() {
      if (!this.active) return;
      const cells = this.cells;
      if (!cells.length) return;
      const width = cells[0].getBoundingClientRect().width;
      this.perView = Math.max(1, Math.round(this.track.clientWidth / Math.max(width, 1)));
      this.pageCount = Math.ceil(cells.length / this.perView);
      this.renderDots();
      if (this.nav) this.nav.hidden = this.pageCount < 2;
      this.syncFromScroll();
    }

    renderDots() {
      if (!this.dotsEl) return;
      const template = this.dataset.labelDot || 'Go to slide #n#';
      if (this.dotsEl.childElementCount === this.pageCount) return;
      this.dotsEl.replaceChildren();
      for (let i = 0; i < this.pageCount; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'floral-carousel__dot';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', template.replace('#n#', String(i + 1)));
        btn.addEventListener('click', () => this.scrollToPage(i));
        this.dotsEl.appendChild(btn);
      }
    }

    syncFromScroll() {
      const cells = this.cells;
      if (!cells.length) return;
      const origin = this.track.getBoundingClientRect().left;
      const pad = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
      let index = 0;
      let closest = Infinity;
      for (let i = 0; i < cells.length; i++) {
        const dist = Math.abs(cells[i].getBoundingClientRect().left - origin - pad);
        if (dist < closest) {
          closest = dist;
          index = i;
        }
      }
      this.setPage(Math.round(index / this.perView));
    }

    setPage(page) {
      const next = Math.max(0, Math.min(this.pageCount - 1, page));
      this.currentPage = next;
      if (this.dotsEl) {
        Array.from(this.dotsEl.children).forEach((dot, i) => {
          dot.setAttribute('aria-selected', i === next ? 'true' : 'false');
        });
      }
      if (this.prevBtn) this.prevBtn.disabled = next === 0;
      if (this.nextBtn) this.nextBtn.disabled = next === this.pageCount - 1;
    }

    go(dir) {
      this.scrollToPage(this.currentPage + dir);
    }

    scrollToPage(page) {
      const cells = this.cells;
      const target = cells[Math.max(0, Math.min(this.pageCount - 1, page)) * this.perView];
      if (!target) return;
      const left = this.track.scrollLeft + (target.getBoundingClientRect().left - this.track.getBoundingClientRect().left);
      this.track.scrollTo({
        left,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    handleKey(event) {
      const rtl = this.isRtl();
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(rtl ? -1 : 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(rtl ? 1 : -1);
      }
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target) || !this.mq.matches) return;
      const cell = event.target.closest('.floral-carousel__cell');
      if (!cell || !this.track.contains(cell)) return;
      cell.scrollIntoView({
        inline: 'start',
        block: 'nearest',
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }
  }

  customElements.define('floral-carousel', FloralCarousel);
})();
